import React, { useState, useEffect, useContext } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiArrowLeft, FiTrendingUp, FiTrendingDown } from 'react-icons/fi';
import { getStockSpecific, getStockTechnicals, getTrendFromSymbol } from '../../services/scanner/scannerApi';
import AddToWatchlistModal from '../../components/watchlist/components/AddToWatchlistModal';
import CustomCard from '../Scanners/components/CustomCard';
import PivotTable from './components/PivotTable';
import IndicatorsTable from './components/IndicatorsTable';
import CandlestickTabContent from './components/CandlestickTabContent';
import TechnicalTabContent from './components/TechnicalTabContent';
import ScannerTabContent from './components/ScannerTabContent';
import HeatMapSection from './components/HeatMapSection';
import StockInfoSkeleton from './components/StockInfoSkeleton';
import TabsSkeleton from './components/TabsSkeleton';
import IndicatorsTableSkeleton from './components/IndicatorsTableSkeleton';
import PivotTableSkeleton from './components/PivotTableSkeleton';
import HeatMapSkeleton from './components/HeatMapSkeleton';
import { toast } from 'react-toastify';
import { useOptionChainScreenContext } from '../OptionChainScreen/context/OptionChainContext';
import { useBuilderContext } from '../StrategyCreator/context/BuilderContext';
import { useBuySellModalContext } from '../../context/BuySellModalContext';
import BuySellModal from '../../modals/BuySellModal';
import WebsocketWrapperContext from '../../context/WebsocketWrapperContext';
import priceup from "../../images/icons/price_up.svg";
import pricedown from "../../images/icons/price_down.svg";
import wlSelected from "../../images/icons/wl_selected.svg";
import wlUnselected from "../../images/icons/wl_unselected.svg";
import CandlestickRangeChart from './components/CandlestickRangeChart';

/**
 * Stock Page - Detailed view for a single stock (matches Flutter layout)
 */
const StockPage = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { setSelectedSymbol, setActiveTab: setOCTab, setSearchSymbol } = useOptionChainScreenContext();
  const { allSymbols } = useBuilderContext();
  const { setBuySellModalData } = useBuySellModalContext();
  const { symbolData, subscribeToSymbols } = useContext(WebsocketWrapperContext);

  const [stockData, setStockData] = useState(null);
  const [technicals, setTechnicals] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loadingStock, setLoadingStock] = useState(true);
  const [loadingTechnicals, setLoadingTechnicals] = useState(false);
  const [loadingTrend, setLoadingTrend] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('candlestick');
  const [showWatchlistPopup, setShowWatchlistPopup] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState('1day');
  const [buySellModalOpen, setBuySellModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState('add');

  // Get stock details from URL params
  const queryParams = new URLSearchParams(location.search);
  const stockCode = queryParams.get('code');
  const stockExchange = queryParams.get('exchange');
  const stockSymbol = queryParams.get('scrip');

  // Store current stock URL in session storage for navigation persistence
  useEffect(() => {
    if (stockCode && stockExchange && stockSymbol) {
      const currentUrl = `/scanners/stock?code=${stockCode}&exchange=${stockExchange}&scrip=${stockSymbol}`;
      sessionStorage.setItem('scanners_lastStockUrl', currentUrl);
    }

    // Listen for popstate event to detect browser back/forward button
    const handlePopState = () => {
      // If back button is pressed, clear stored URL and set flag to prevent redirect
      sessionStorage.removeItem('scanners_lastStockUrl');
      sessionStorage.setItem('scanners_preventRedirect', 'true');
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [stockCode, stockExchange, stockSymbol]);

  useEffect(() => {
    if (stockSymbol) {
      loadStockData();
      loadTrendData();
    }
  }, [stockSymbol]);

  useEffect(() => {
    if (stockSymbol) {
      loadTechnicals();
    }
  }, [stockSymbol, selectedTimeframe]);

  // Subscribe to websocket for real-time price updates
  useEffect(() => {
    if (stockCode && stockExchange) {
      subscribeToSymbols([stockCode], stockExchange);
    }
  }, [stockCode, stockExchange]);

  const loadStockData = async () => {
    setLoadingStock(true);
    setError(null);

    try {
      const data = await getStockSpecific(stockSymbol, 'Intraday');
      console.log('Stock data loaded:', data);
      setStockData(data);
    } catch (err) {
      console.error('Error loading stock data:', err);
      setError('Failed to load stock data');
      toast.error('Failed to load stock data');
    } finally {
      setLoadingStock(false);
    }
  };

  const loadTechnicals = async () => {
    setLoadingTechnicals(true);
    try {
      const data = await getStockTechnicals(stockSymbol, selectedTimeframe);
      console.log('Technicals loaded:', data);
      setTechnicals(data);
    } catch (err) {
      console.error('Error loading technicals:', err);
    } finally {
      setLoadingTechnicals(false);
    }
  };

  const loadTrendData = async () => {
    setLoadingTrend(true);
    try {
      const data = await getTrendFromSymbol(stockSymbol);
      console.log('Trend loaded:', data);
      setTrend(data);
    } catch (err) {
      console.error('Error loading trend:', err);
    } finally {
      setLoadingTrend(false);
    }
  };

  const handleAddToWatchlist = () => {
    setShowWatchlistPopup(true);
  };

  const handleBuySell = (isBuy) => {
    const ltpPrice = getLivePrice();

    const data_object = {
      side: isBuy ? "Buy" : "Sell",
      instrumentName: stockData?.company_name || stockData?.companyName || stockSymbol,
      lotSize: 1,
      lot_size: 1,
      productType: "Normal",
      tradingSymbol: stockSymbol,
      expiryDate: null,
      code: stockCode,
      price: ltpPrice,
      trigger_price: ltpPrice,
      buy_sell: isBuy ? "B" : "S",
      underlying_code: stockCode,
      strike: null,
      quantity: 1,
      exchange: stockExchange,
      scrip: stockSymbol,
      lot_multiplier: 1,
      isCall: null,
      isFut: false,
    };

    setBuySellModalData([data_object]);
    setModalAction('add');
    setBuySellModalOpen(true);
  };

  const handleBuyClick = () => handleBuySell(true);
  const handleSellClick = () => handleBuySell(false);

  const closeBuySellModal = () => {
    setBuySellModalOpen(false);
  };

  const fetchOrders = () => {
    // Placeholder for order fetching logic
  };

  const handleOptionChain = () => {
    console.log('Navigating to option chain for', stockSymbol);
    // Find the symbol in allSymbols and navigate to option chain with URL param
    const matchedSymbol = allSymbols?.find(symbol => symbol.scrip === stockSymbol);
    if (matchedSymbol) {
      // Navigate with symbol as URL parameter - this triggers the preSelectedSymbol logic
      navigate(`/optionchain/${stockSymbol}`);
    } else {
      toast.error('Symbol not found in option chain');
    }
  };

  const getInstrument = () => {
    return {
      tradingSymbol: stockSymbol,
      trading_symbol: stockSymbol,
      exchange: stockExchange,
      exch: stockExchange,
      code: stockCode,
      companyName: stockData?.company_name || stockData?.companyName || stockSymbol,
      formatted_ins_name: stockData?.company_name || stockData?.companyName || stockSymbol,
      name: stockData?.company_name || stockData?.companyName || stockSymbol,
    };
  };

  const formatNumber = (num) => {
    if (num == null) return '-';
    return parseFloat(num).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatChange = (change) => {
    if (change == null) return '-';
    const value = parseFloat(change);
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  const getChangeColor = (change) => {
    if (change == null) return '';
    const value = parseFloat(change);
    if (value > 0) return 'text-p150';
    if (value < 0) return 'text-red';
    return '';
  };

  // Get real-time data from websocket
  const getLivePrice = () => {
    return symbolData[stockCode]?.ltp || stockData?.close || 0;
  };

  const getLivePriceChange = () => {
    return symbolData[stockCode]?.change_per || stockData?.change_per || stockData?.changePer || 0;
  };

  const getLivePriceChangeValue = () => {
    return symbolData[stockCode]?.change || stockData?.change || 0;
  };

  const getLiveOHLC = () => {
    return {
      open: symbolData[stockCode]?.open || stockData?.open || 0,
      high: symbolData[stockCode]?.high || stockData?.high || 0,
      low: symbolData[stockCode]?.low || stockData?.low || 0,
      close: symbolData[stockCode]?.ltp || stockData?.close || 0,
    };
  };

  if (error) {
    return (
      <div className={`min-h-screen mt-16 pb-[60px] px-10 ${theme === 'dark' ? 'bg-[#272727]' : 'bg-[#EBEBEB]'}`}>
        <div className="text-center py-12">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={() => navigate('/scanners')}
            className={`px-4 py-2 rounded-[6px] ${theme === 'dark' ? 'bg-[#BCEF3F] text-[#0F0F0F]' : 'bg-[#0F0F0F] text-white'
              }`}
          >
            Back to Scanners
          </button>
        </div>
      </div>
    );
  }

  const isIndex = stockData?.is_index || stockData?.isIndex;
  const isFno = stockData?.is_fno || stockData?.isFno;

  return (
    <div className={`min-h-screen mt-10 pb-[60px] px-2`}>
      {/* Back Button */}
      <button
        onClick={() => {
          // Clear stored URL and set flag to prevent redirect when going back to scanners
          sessionStorage.removeItem('scanners_lastStockUrl');
          sessionStorage.setItem('scanners_preventRedirect', 'true');
          // Navigate to scanners
          navigate('/scanners');
        }}
        className={`mt-3 mb-4 flex items-center gap-2 px-3 py-2 rounded-[6px] text-sm font-medium ${theme === 'dark'
          ? 'bg-[#0F0F0F] hover:bg-[#1F1F1F] text-white shadow-[0_2px_5px_1px_rgba(175,175,175,0.1)]'
          : 'bg-white hover:bg-gray-50 text-black shadow-[0_2px_5px_1px_rgba(87,87,87,0.1)]'
          }`}
      >
        <FiArrowLeft size={16} />
        Back to Scanners
      </button>

      {/* Two-Column Layout */}
      <div className="flex flex-row items-start gap-5 mb-4">
        {/* Left Column - Stock Info */}
        <div className="w-[360px] flex-shrink-0">
          {loadingStock ? (
            <StockInfoSkeleton />
          ) : (
            <CustomCard className="p-6">
              {/* Stock Header */}
              <div className="flex items-start justify-between mb-1">
                <div>
                  <h1 className="text-[13px] font-medium text-n500">
                    {stockSymbol} • {stockExchange}
                  </h1>
                  <p className="text-xl font-semibold mt-2 text-p300">
                    {stockData?.company_name || stockData?.companyName || stockSymbol}
                  </p>
                </div>

                <button
                  onClick={handleAddToWatchlist}
                  className={`p-2 rounded-[6px] ${theme === 'dark' ? 'hover:bg-[#1F1F1F]' : 'hover:bg-[#F5F5F5]'
                    }`}
                >
                  <img
                    src={wlUnselected}
                    alt="Add to Watchlist"
                    className="w-7 h-7"
                  />
                </button>
              </div>

              {/* Price + Change */}
              <div className="flex items-center mb-3">
                <span className="text-2xl font-bold text-p300">
                  ₹{formatNumber(getLivePrice())}
                </span>
                <span className={`ml-2 pt-1 text-[13px]  font-semibold ${getChangeColor(getLivePriceChange())}`}>
                  {getLivePriceChange() >= 0 ? '+' : ''}{getLivePriceChange()}% ({formatNumber(getLivePriceChangeValue())})
                </span>
              </div>

              {/* Divider */}
              <div
                className={`border-t my-4 -mx-6 ${theme === 'dark' ? 'border-[#1F1F1F]' : 'border-[#EBEBEB]'
                  }`}
              />

              {/* OHLC */}
              <CandlestickRangeChart
                high={getLiveOHLC().high}
                low={getLiveOHLC().low}
                open={getLiveOHLC().open}
                close={getLiveOHLC().close}
                formatNumber={formatNumber}
              />

              {/* Divider */}
              <div
                className={`border-t my-4 -mx-6 ${theme === 'dark' ? 'border-[#1F1F1F]' : 'border-[#EBEBEB]'
                  }`}
              />

              {/* Buy / Sell */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <button
                  onClick={handleBuyClick}
                  disabled={isIndex}
                  className={`h-10 rounded-[6px] text-white text-sm font-semibold ${isIndex
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-p150'
                    }`}
                >
                  Buy
                </button>

                <button
                  onClick={handleSellClick}
                  disabled={isIndex}
                  className={`h-10 rounded-[6px] text-white text-sm font-semibold ${isIndex
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-red hover:bg-red-600'
                    }`}
                >
                  Sell
                </button>
              </div>

              {/* Option Chain */}
              <button
                onClick={handleOptionChain}
                disabled={!isFno}
                className={`w-full h-10 mb-4 rounded-[6px] text-sm font-medium ${!isFno
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : 'text-p300 bg-p400 border border-n200'
                  }`}
              >
                Option Chain
              </button>

              {/* Today’s Trend */}
              {trend && (
                <div className="flex items-center justify-center gap-3">
                  <span
                    className={`text-[13px] font-normal  text-n500
                    }`}
                  >
                    Today&apos;s Trend
                  </span>

                  <div className="flex items-center gap-2">
                    {trend.trend === 'Bullish' ? (
                      <img
                        src={priceup}
                        alt="Bullish"
                        className="w-4 h-4"
                        width={20}
                        height={20}
                      />
                    ) : (
                      <img
                        src={pricedown}
                        alt="Bearish"
                        className="w-4 h-4"
                      />
                    )}

                    <span
                      className={`text-[13px] font-medium`}
                    >
                      {trend.trend}
                    </span>
                  </div>
                </div>
              )}
            </CustomCard>
          )}
        </div>

        {/* Right Column - Tabs */}
        <div className="flex-1">
          {loadingStock ? (
            <TabsSkeleton />
          ) : (
            <>
              {/* Tab Headers - Outside the card */}
              <div className={`p-1 w-fit rounded ${theme === 'dark' ? 'bg-p400' : 'bg-p400'} mb-3`} style={{ height: '40px' }}>
                <div className="flex h-full">
                  <button
                    onClick={() => setActiveTab('candlestick')}
                    className={`flex-1 px-4 text-xs font-semibold rounded transition-colors ${activeTab === 'candlestick'
                      ? 'bg-n200 bg-opacity-50 text-n900'
                      : 'text-n500 hover:text-n900'
                      }`}
                  >
                    Candlestick
                  </button>
                  <button
                    onClick={() => setActiveTab('technical')}
                    className={`flex-1 px-4 text-xs font-semibold rounded transition-colors ${activeTab === 'technical'
                      ? 'bg-n200 bg-opacity-50 text-n900'
                      : 'text-n500 hover:text-n900'
                      }`}
                  >
                    Technical
                  </button>
                  <button
                    onClick={() => setActiveTab('scanner')}
                    className={`flex-1 px-4 text-xs font-semibold rounded transition-colors ${activeTab === 'scanner'
                      ? 'bg-n200 bg-opacity-50 text-n900'
                      : 'text-n500 hover:text-n900'
                      }`}
                  >
                    Scanner
                  </button>
                </div>
              </div>

              {/* Tab Content Card */}
              <CustomCard style={{ height: '344px', padding: 0 }}>
                {/* Tab Content */}
                <div style={{ height: '344px' }} className="py-4">
                  {activeTab === 'candlestick' && (
                    <CandlestickTabContent
                      data={stockData?.candlestick_data || stockData?.candlestickData}
                    />
                  )}

                  {activeTab === 'technical' && (
                    <TechnicalTabContent
                      pivotLevels={stockData?.pivot_levels || stockData?.pivotLevels}
                    />
                  )}

                  {activeTab === 'scanner' && (
                    <ScannerTabContent
                      data={stockData?.scanner_data || stockData?.scannerData}
                    />
                  )}
                </div>
              </CustomCard>
            </>
          )}
        </div>
      </div>

      {/* Full-Width Sections Below */}

      {/* Technical Indicators Section */}

      {loadingTechnicals ? (
        <div className="mb-4">
          <div className="flex items-center mb-4">
            <div className="h-6 w-48 bg-n200 rounded animate-pulse"></div>
            <div className="ml-auto h-9 w-32 bg-n200 rounded-[6px] animate-pulse"></div>
          </div>
          <IndicatorsTableSkeleton />
        </div>
      ) : technicals && (
        <div className="mb-4">
          <div className="flex items-center mb-4">
            {/* Left: title + updated */}
            <div className="flex items-center gap-3">
              <h2
                className={`text-lg font-bold ${theme === 'dark' ? 'text-[#EDEDED]' : 'text-[#272727]'
                  }`}
              >
                Technical Indicators
              </h2>

              {technicals.last_updated && (
                <p
                  className={`text-xs ${theme === 'dark' ? 'text-[#7F7F7F]' : 'text-[#878787]'
                    }`}
                >
                  Last updated: {technicals.last_updated}
                </p>
              )}
            </div>

            {/* Right: timeframe selector (dynamic) */}
            <select
              value={selectedTimeframe}
              onChange={(e) => setSelectedTimeframe(e.target.value)}
              className={`ml-auto h-9 px-3 rounded-[6px] border text-sm font-medium ${theme === 'dark'
                ? 'bg-[#0F0F0F] border-[#373737] text-[#AFAFAF]'
                : 'bg-white border-[#CFCFCF] text-[#575757]'
                } focus:outline-none`}
            >
              {technicals.timeframes?.map((tf) => (
                <option key={tf.value} value={tf.value}>
                  {tf.display}
                </option>
              ))}
            </select>
          </div>

          <IndicatorsTable indicators={technicals.table_data} />
        </div>
      )}


      {/* Pivot Levels Section */}
      {loadingStock ? (
        <div className="mb-4">
          <div className="h-6 w-32 bg-n200 rounded animate-pulse mb-4"></div>
          <PivotTableSkeleton />
        </div>
      ) : (stockData?.pivot_levels || stockData?.pivotLevels) && (
        <div className="mb-4">
          <div className="mb-4 flex items-center gap-3">
            <h2
              className={`text-lg font-bold ${theme === 'dark' ? 'text-[#EDEDED]' : 'text-[#272727]'
                }`}
            >
              Pivot Levels
            </h2>

            {stockData?.pivot_levels?.[0]?.updated_on && (
              <p
                className={`text-xs ${theme === 'dark' ? 'text-[#7F7F7F]' : 'text-[#878787]'
                  }`}
              >
                Last updated: {stockData.pivot_levels[0].updated_on}
              </p>
            )}
          </div>

          <PivotTable
            pivotLevels={stockData.pivot_levels || stockData.pivotLevels}
          />
        </div>
      )}

      {/* Heat Map Section */}
      {loadingStock ? (
        <HeatMapSkeleton />
      ) : (stockData?.heat_map || stockData?.heatMap) && (stockData.heat_map?.length > 0 || stockData.heatMap?.length > 0) && (
        <HeatMapSection
          heatMapData={stockData.heat_map || stockData.heatMap}
          updatedOn={stockData?.pivot_levels?.[0]?.updated_on || stockData?.pivotLevels?.[0]?.updatedOn}
        />
      )}

      {/* Watchlist Modal */}
      <AddToWatchlistModal
        isOpen={showWatchlistPopup}
        onClose={() => setShowWatchlistPopup(false)}
        instrument={getInstrument()}
      />

      {/* Buy/Sell Modal */}
      <BuySellModal
        modalOpen={buySellModalOpen}
        modalClose={closeBuySellModal}
        isOrders={false}
        setIsBuySellModalOpen={setBuySellModalOpen}
        fetchOrders={fetchOrders}
        initialData={null}
        action={modalAction}
      />
    </div>
  );
};

export default StockPage;
